/**
 * DatabaseTable.tsx
 *
 * This is a data-driven component that is used _LOTS_ of places. Please do
 * not add single-use code here. This is meant to be completely configurable
 * by the schema it is passed. If you're working with one of the standard
 * database schemas defined in InstanceSchema, you can apply any overrides
 * that need to return JSX where FrontendInstanceSchema is defined below.
 *
 * Please search the codebase for other examples of using this component before
 * making changes to this file, the needed feature may already be possible.
 */
import { cn } from '@/lib/utils';
import { Loading } from '@chiroup/components/Loading';
import { InstanceSchema, InstanceSchemaType } from '@chiroup/core/types/DatabaseFeatureTypes';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NoRowsFound from './NoRowsFound';
import Pagination from './Pagination';
import SectionContainer from './SectionContainer';

type DatabaseTableOptions = {
  includeHeader?: boolean;
  sectionClassName?: string;
  outerClassName?: string;
  evenClassName?: string;
  oddClassName?: string;
  defaultThClassName?: string;
  roundedHeader?: boolean;
  defaultTdClassName?: string;
  thBorder?: string;
  noRowsFoundComponent?: React.ReactNode;
  clickableHoverClassName?: string;
};

type Props = {
  instanceTitle: string;
  rows: any[] | null;
  schema: InstanceSchemaType;
  page?: number;
  setPage?: Dispatch<SetStateAction<number>>;
  count: number;
  limit?: number;
  isFetching?: boolean;
  onRowClick?: (row: any, index: number) => void;
  cellClassName?: string;
  clickable?: boolean;
  columnMetadata?: { [key: string]: { content: React.ReactNode } };
  parentClassName?: string | undefined;
  onClickLink?: string;
  options?: DatabaseTableOptions;
  clientSide?: boolean;
  clientPage?: number;
  setClientPage?: Dispatch<SetStateAction<number>>;
  onSort?: (key: string, dir: 'asc' | 'desc', multi?: boolean) => void;
  sortState?: { key: string; dir: 'asc' | 'desc' }[] | null;
};

const LoadingOverlay = ({ isFetching }: { isFetching: boolean }) => (
  <div
    className={cn(
      'absolute z-0 left-0 top-0 w-full h-full bg-white dark:bg-dark-gray-700 font-bold flex justify-center items-center transition-opacity ease-in-out duration-500',
      isFetching ? 'opacity-90 cursor-wait' : 'opacity-0 pointer-events-none'
    )}
  >
    <Loading color="text-gray-500" />
  </div>
);

export const DatabaseTable: React.FC<Props> = ({
  instanceTitle,
  rows,
  schema,
  page,
  setPage = () => {},
  count,
  limit,
  isFetching = false,
  onRowClick,
  cellClassName = 'text-sm font-normal text-gray-900',
  clickable = true,
  columnMetadata = {},
  parentClassName = undefined,
  onClickLink,
  options = {},
  clientSide = false,
  onSort,
  sortState: sortStateProp,
}) => {
  const sortState = Array.isArray(sortStateProp) ? sortStateProp : null;

  // Client-side pagination state (internal fallback)
  const [internalClientPage, setInternalClientPage] = useState(1);

  // Use external client page props if provided, otherwise fall back to internal state
  const actualClientPage = page ?? internalClientPage;
  const actualSetClientPage = setPage ?? setInternalClientPage;

  // Compute effective values based on clientSide prop
  const effectivePage = clientSide ? actualClientPage : page || 1;
  const effectiveSetPage = clientSide ? actualSetClientPage : setPage;
  const effectiveCount = clientSide ? rows?.length || 0 : count;

  // Compute displayed rows for client-side pagination
  const displayedRows = useMemo(() => {
    if (!clientSide || !rows || !limit) {
      return rows;
    }

    const startIndex = (effectivePage - 1) * limit;
    const endIndex = startIndex + limit;

    return rows.slice(startIndex, endIndex);
  }, [clientSide, rows, limit, effectivePage]);

  options = {
    includeHeader: true,
    sectionClassName:
      'sm:shadow border border-gray-300 dark:border-dark-gray-600 rounded-md print:rounded-none overflow-hidden',
    outerClassName: 'overflow-x-auto relative',
    evenClassName: 'bg-white dark:bg-dark-gray-800',
    oddClassName: 'bg-gray-50 dark:bg-dark-gray-700',
    defaultThClassName:
      'px-6 py-3 text-left text-sm text-gray-900 dark:text-dark-gray-50 tracking-wide print:text-gray-800 whitespace-nowrap',
    defaultTdClassName: 'px-6 py-4 align-top',
    noRowsFoundComponent: (
      <NoRowsFound name={instanceTitle} create={() => navigate(`${pathname}?id=add`)} />
    ),
    roundedHeader: false,
    clickableHoverClassName: 'cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-gray-700',
    ...options,
  };

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const defaultOnClick = (row: any, index: number) => {
    navigate(`${onClickLink || pathname}?id=${row.ID || row.id || index}`);
  };

  const columns = useMemo(() => {
    return (
      Object.keys(schema)?.filter(
        key => schema[key].showOnList !== false && schema[key].hidden !== true
      ) || []
    );
  }, [schema]);

  const stickyColumn = useMemo(() => {
    // Only allow the last column to be sticky
    const lastColumn = columns[columns.length - 1];

    return lastColumn && schema[lastColumn]?.sticky ? lastColumn : null;
  }, [columns, schema]);

  if (!rows) {
    if (isFetching) {
      // should only display on initial data fetch
      return (
        <SectionContainer parentClassName={parentClassName} className={options.sectionClassName}>
          <div className={cn(options?.outerClassName, 'relative min-h-36')}>
            <LoadingOverlay isFetching={isFetching} />
          </div>
        </SectionContainer>
      );
    }

    return null;
  }

  const rowsToRender = displayedRows || rows;

  return (
    <SectionContainer parentClassName={parentClassName} className={options.sectionClassName}>
      {columns?.length &&
        (!rowsToRender?.length ? (
          options?.noRowsFoundComponent
        ) : (
          <div className={options?.outerClassName}>
            <LoadingOverlay isFetching={isFetching} />
            <table className="min-w-full dark:divide-dark-gray-600">
              {!!options?.includeHeader && (
                <thead className="bg-gray-200 dark:bg-dark-gray-600">
                  <tr key="thead-key">
                    {columns.map((key, keyIndex) => (
                      <th
                        scope="col"
                        key={`th-${key}-${keyIndex}`}
                        className={cn(
                          'p-0',
                          stickyColumn === key
                            ? 'sticky right-0 z-10 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.1)] border-l border-gray-300 dark:border-dark-gray-500 bg-gradient-to-r from-transparent to-gray-200 dark:to-dark-gray-600 to-[10px]'
                            : ''
                        )}
                      >
                        <div
                          className={cn(
                            options?.defaultThClassName,
                            schema[key]?.thClassName || '',
                            options?.roundedHeader && keyIndex === 0 ? 'rounded-l-md' : '',
                            options?.roundedHeader && keyIndex === columns.length - 1
                              ? 'rounded-r-md'
                              : '',
                            options?.thBorder
                              ? `${options.thBorder} ${keyIndex === 0 ? 'border-l' : ''} ${
                                  keyIndex === columns.length - 1 ? 'border-r' : ''
                                } border-t border-b`
                              : '',
                            schema[key]?.sortKey && onSort ? 'cursor-pointer select-none' : ''
                          )}
                          onClick={
                            schema[key]?.sortKey && onSort
                              ? (e: React.MouseEvent) => {
                                  const sk = schema[key].sortKey as string;
                                  const entry = sortState?.find(s => s.key === sk);
                                  const nextDir: 'asc' | 'desc' =
                                    entry?.dir === 'asc' ? 'desc' : 'asc';
                                  onSort(sk, nextDir, e.shiftKey);
                                }
                              : undefined
                          }
                        >
                          {columnMetadata[key]?.content ? (
                            <div className="whitespace-normal">{columnMetadata[key].content}</div>
                          ) : schema[key]?.sortKey && onSort ? (
                            (() => {
                              const entry = sortState?.find(s => s.key === schema[key].sortKey);

                              return (
                                <span className="inline-flex items-center">
                                  {schema[key].label}
                                  {entry ? (
                                    entry.dir === 'asc' ? (
                                      <ChevronUpIcon className="w-4 h-4 inline-block ml-1 text-primary-900" />
                                    ) : (
                                      <ChevronDownIcon className="w-4 h-4 inline-block ml-1 text-primary-900" />
                                    )
                                  ) : (
                                    <ChevronDownIcon className="w-4 h-4 inline-block ml-1 text-gray-900/30" />
                                  )}
                                </span>
                              );
                            })()
                          ) : (
                            schema[key].label
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rowsToRender.map((row, i) => (
                  <tr
                    key={`data-row-${i}`}
                    onClick={() => {
                      if (!clickable) return;
                      onRowClick ? onRowClick(row, i) : defaultOnClick(row, i);
                    }}
                    className={cn(
                      `border-b-2 border-transparent ${
                        clickable ? options?.clickableHoverClassName : ''
                      }`,
                      i % 2 === 0 ? options?.evenClassName : options?.oddClassName,
                      'hover:bg-gray-100 dark:hover:bg-dark-gray-500'
                    )}
                  >
                    {columns.map((key, keyIndex) => (
                      <td
                        className={cn(
                          schema[key]?.tdClassName || '',
                          stickyColumn === key
                            ? 'sticky right-0 z-10 px-4 bg-gradient-to-r from-transparent to-white dark:to-dark-gray-800 to-[10px]'
                            : options?.defaultTdClassName
                        )}
                        key={`${i}.${keyIndex}`}
                      >
                        <div
                          className={cn(
                            schema[key]?.tdClassName ?? cellClassName,
                            ' dark:text-dark-gray-50',
                            schema[key]?.tdDivClassName || ''
                          )}
                        >
                          {schema[key] && schema[key].td
                            ? (
                                schema[key].td ||
                                function (row: any, ord: string) {
                                  return row[ord] as string;
                                }
                              )(row, key)
                            : row[key]}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {!!limit && (
        <div className="overflow-x-auto">
          <Pagination
            setPage={effectiveSetPage}
            currentPage={effectivePage}
            count={effectiveCount}
            limit={limit}
          />
        </div>
      )}
    </SectionContainer>
  );
};

/**
 * This is where to drop any well-known schema overrides for the UI. Mostly,
 * this supports adding custom JSX to the table cells like the DatabaseTable
 * component supports for other non-standard schemas. The InstanceSchema data
 * structure is for front-end or backend, but the _td_ functions support JSX
 * as well which is no bueno in .ts files.
 *
 * Note this will REPLACE anything in the InstanceSchema.
 */
const FrontendInstanceSchema = InstanceSchema;

FrontendInstanceSchema.payors.legalName.td = (row: any, ord?: string) => {
  ord = ord || 'legalName';

  return (
    <div className="text-sm">
      <span className="block">{row[ord]}</span>
      <div className="flex mt-2 space-x-2">
        {row.inNetwork && (
          <span className="rounded-lg bg-primary-500 py-1 px-2 text-[10px] leading-[10px] font-light text-white whitespace-nowrap">
            In Network
          </span>
        )}
        {!row.electronicBilling && (
          <span className="rounded-lg bg-yellow-300 py-1 px-2 text-[10px] leading-[10px] font-light text-gray-600 whitespace-nowrap">
            Paper Billing
          </span>
        )}
        {!row.billable && (
          <span className="rounded-lg bg-red-500 py-1 px-2 text-[10px] leading-[10px] font-light text-white whitespace-nowrap">
            Non-billable
          </span>
        )}
      </div>
    </div>
  );
};

FrontendInstanceSchema.payors.address1.td = (row: any, ord?: string) => {
  ord = ord || 'address1';

  return (
    <div className="flex flex-col">
      {row.address1 ? <div>{row.address1}</div> : null}
      {row.address2 ? <div>{row.address2}</div> : null}
      {row.city ? <div>{row.city}</div> : null}
      {row.state ? <div>{row.state}</div> : null}
      {row.zip ? <div>{row.zip}</div> : null}
    </div>
  );
};
